import React, { useEffect, useRef, useState } from "react";
import { MainLayoutType } from "@/types/layout";
import { Footer } from "@/components/layout/Footer/Footer";
import useMenusProvider from "@/core/dataProviders/providers/menu/useMenusProvider";
import { useLockedBody } from "@/hooks/useLockBody";
import useSettingsProvider from "@/core/dataProviders/providers/app/useSettingsProvider";
import { ScrollBar } from "@/components/ui-kit/ScrollBar";
import { PostSidebar } from "@/components/layout/Sidebar/PostSidebar";
import { FixedNavbar } from "@/components/layout/Sidebar/FixedNavbar";
import Head from "next/head";
import autoAnimate from "@formkit/auto-animate";
import { FloatingPlayer } from "@/components/modules/Audio/FloatingPlayer";
import { Toaster } from "sonner";
import { useDarkTheme } from "@/hooks/useDarkTheme";
import { AdvertisementBlock } from "@/components/layout/AdvertisementBlock";
import { OfflineWarningBanner } from "@/components/ui-kit/OfflineWarningBanner";
import { BannersType } from "@/types/ui-kit/ui";
import { useBrandingAdsChecker } from "@/hooks/useBrandingAdsChecker";
import classNames from "classnames";
import { useIsAppOnline } from "@/hooks/useIsAppOnline";

export const PostLayout: React.FC<MainLayoutType> = ({ children, title, post }) => {
  const [menu] = useMenusProvider();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [settings] = useSettingsProvider();
  const [openedBurgerMenu, setOpenedBurgerMenu] = useState<boolean>(false);
  const isAppOnline = useIsAppOnline();
  const setLockedBody = useLockedBody();
  const { displayBranding } = useBrandingAdsChecker();

  const { currentTheme } = useDarkTheme();

  useEffect(() => {
    if (title?.length) {
      document.title = title;
    }
  }, [title]);

  const parent = useRef(null);

  useEffect(() => {
    parent.current && autoAnimate(parent.current);
  }, [parent]);

  return (
    <div className={currentTheme}>
      <Head>
        <title>{title ?? "Magazín Reportér"}</title>
        <link rel="icon" href={settings?.favicon} />
        <link rel="apple-touch-icon" sizes="180x180" href="/icons/ios-180x180.png" />
        <link rel="manifest" href="/manifest.json" />
        <meta name="application-name" content="Reportér Magazín" />
        <meta name="theme-color" content="#E3662F" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="Reportér Magazín" />
        <meta name="mobile-web-app-capable" content="yes" />`
      </Head>

      {displayBranding && <AdvertisementBlock id={"protag-branding"} />}

      <div className="reporter-body">
        <div className={`${!displayBranding ? "w-full" : "mx-auto w-full max-w-[1033px] xl:mt-40"} dark:bg-dark`}>
          <div>
            <ScrollBar contentRef={contentRef} />
            <PostSidebar setOpenedMenu={setOpenedBurgerMenu} openedBurgerMenu={openedBurgerMenu} post={post} />
            <main
              ref={contentRef}
              className={classNames("min-h-screen w-full lg:px-4 max-lg:landscape:pl-[62px]", {
                "pt-[39px] lg:pt-[44px]": !displayBranding,
              })}
            >
              <div>{children}</div>
              <AdvertisementBlock id={"protag-Footer_AD"} />
            </main>
            {!isAppOnline && <OfflineWarningBanner type={BannersType.Primary} />}
          </div>

          <Footer navigationItems={menu?.["footer-navigation-1"]!} />

          <FixedNavbar
            post={post}
            openedBurgerMenu={openedBurgerMenu}
            isPostDetail={true}
            showBurgerMenu={() => {
              setOpenedBurgerMenu(!openedBurgerMenu);
              setLockedBody(!openedBurgerMenu);
            }}
            title={title ?? "Magazín Reportér"}
          />
        </div>
        <div ref={parent}>
          <div className="fixed bottom-[60.5px] left-0 right-0 z-50 mx-auto w-full md:w-[600px] lg:bottom-2">
            <FloatingPlayer />
          </div>
        </div>
        <Toaster position="bottom-center" className="!w-screen !max-w-[600px] md:[&>li]:!w-full" />
      </div>
    </div>
  );
};
